import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  type?: 'website' | 'article';
  canonical?: string;
  image?: string;
  schema?: object;
}

function SEO({
  title,
  description,
  type = 'website',
  canonical,
  image,
  schema
}: SEOProps) {
  const siteName = "elektraaanleggen.nl";
  const defaultImage = "/images/banner.png";
  const fullTitle = `${title} | ${siteName}`;
  const currentUrl = typeof window !== 'undefined' ? window.location.pathname : '';

  // Map of pages to keywords with an index signature for dynamic keys
  const pageKeywords: { [key: string]: string } = {
    "/": "elektricien, elektra, groepenkast, renovatie, zonnepanelen, laadpaal, elektrische installatie",
    "/offerte": "gratis offerte, elektricien offerte, prijsopgave elektra, installatie offerte",
    "/groepenkast-vervangen": "groepenkast vervangen, groepenkast, elektrotechnisch werk",
    "/elektra-aanleggen": "elektra aanleggen, nieuwe installatie, elektrotechniek",
    "/kookgroep-aansluiten": "kookgroep aansluiten, kookgroep installatie, elektra kookgroep",
    "/elektra-renovatie": "elektra renovatie, woning renovatie, elektrische renovatie",
    "/3-fase-aansluiting": "3 fase aansluiting, krachtstroom, 3 fase elektra",
    "/laadpaal": "laadpaal, laadpaal installatie, elektrische voertuigen",
    "/zonnepanelen": "zonnepanelen, zonnepanelen installatie, duurzame energie",
    "/klantenservice": "klantenservice, contact elektra, service aanvragen",
    "/ons-verhaal": "over ons, elektra bedrijf, onze missie",
    "/vacatures": "vacatures elektra, werken bij elektra, vacatures techniek",
    "/begrippen": "elektrische begrippen, uitleg elektra, termen elektrotechniek",
    "/inspectie": "elektra inspectie, elektrische controle, veiligheid elektra",
    "/regios": "elektricien regio, elektrische diensten Nederland",
    "/privacy": "privacybeleid, persoonsgegevens, privacy elektra",
    "/veelgestelde-vragen": "FAQ, veelgestelde vragen, elektra vragen",
    "/voorwaarden": "voorwaarden, gebruiksvoorwaarden, algemene voorwaarden",
    "/contact": "contact elektra, contact formulier, bellen elektra",
    "/stopcontacten-plaatsen": "stopcontacten plaatsen, elektra installaties, stroompunten",
    "/bedankt-offerte": "bedankt offerte, offerte aanvraag, bedankt aanvraag",
    "/cookies": "cookies, privacy cookies, tracking cookies",
    "/sitemap": "site map, sitemap elektra, website structuur"
  };

  // Get keywords based on current path, or fallback to default
  const keywords = pageKeywords[currentUrl] || "elektricien, elektra, installatie, renovatie";

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical || currentUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:url" content={canonical || currentUrl} />
      <meta property="og:locale" content="nl_NL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || defaultImage} />
      <meta name="twitter:site" content="@elektraaanleg" />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

export default SEO